import { useState } from "react";

import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from "@/components/ui/popover";

import {
  PlanHubButton,
  PlanHubButtonSkeleton,
} from "./components/PlanHubButton";
import { PlanPopoverContent } from "./components/PlanPopoverContent";
import { usePlanHub } from "./use-plan-hub";

interface PlanHubWidgetProps {
  className?: string;
}

/**
 * Header plan hub: a usage-ring pill that opens the seat + AI token panel.
 *
 * Callers mount this only for admins with subscription access, so every action
 * here (seats, top-up, billing) is assumed reachable.
 */
export const PlanHubWidget = ({ className }: PlanHubWidgetProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    resources,
    plan,
    ringPercentage,
    ringStatus,
    isLoading,
    hasSeats,
    goToSeats,
    goToTokens,
    goToBilling,
  } = usePlanHub();

  const navigateAndClose = (go: () => void) => () => {
    setIsOpen(false);
    go();
  };

  if (isLoading) {
    return <PlanHubButtonSkeleton className={className} />;
  }

  if (!resources.length) return null;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <PlanHubButton
          percentage={ringPercentage}
          status={ringStatus}
          className={className}
        />
      </PopoverTrigger>

      <PopoverPortal>
        <PopoverContent
          align="end"
          sideOffset={8}
          className="z-50 w-80 overflow-hidden rounded-xl border-border/50 p-0 shadow-xl"
        >
          <PlanPopoverContent
            plan={plan}
            resources={resources}
            ringPercentage={ringPercentage}
            ringStatus={ringStatus}
            onUpgradeSeats={hasSeats ? navigateAndClose(goToSeats) : undefined}
            onBuyTokens={navigateAndClose(goToTokens)}
            onManageBilling={navigateAndClose(goToBilling)}
          />
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
};
